import { CaptionedBlockComponent } from '@blocksuite/affine-components/caption';
import { whenHover } from '@blocksuite/affine-components/hover';
import { LoadingIcon } from '@blocksuite/affine-components/icons';
import { Peekable } from '@blocksuite/affine-components/peek';
import { ResourceController } from '@blocksuite/affine-components/resource';
import type { ImageBlockModel } from '@blocksuite/affine-model';
import { FetchUtils } from '@blocksuite/affine-shared/adapters';
import { ImageSelection } from '@blocksuite/affine-shared/selection';
import {
  BlockElementCommentManager,
  ToolbarRegistryIdentifier,
} from '@blocksuite/affine-shared/services';
import {
  formatSize,
  getFilenameFromContentDisposition,
} from '@blocksuite/affine-shared/utils';
import { IS_MOBILE } from '@blocksuite/global/env';
import { sha } from '@blocksuite/global/utils';
import { BrokenImageIcon, ImageIcon } from '@blocksuite/icons/lit';
import { BlockSelection } from '@blocksuite/std';
import { computed } from '@preact/signals-core';
import { cssVarV2 } from '@toeverything/theme/v2';
import { html } from 'lit';
import { query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { when } from 'lit/directives/when.js';

import type { ImageBlockPageComponent } from './components/page-image-block';
import {
  copyImageBlob,
  downloadImageBlob,
  refreshData,
  turnImageIntoCardView,
} from './utils';
import { maybeCompressLocalImageBlob } from './adapters/utils';

// 🔥 全局加载状态追踪，防止重复加载
const pendingCdnLoads = new Set<string>();

@Peekable({
  enableOn: () => !IS_MOBILE,
})
export class ImageBlockComponent extends CaptionedBlockComponent<ImageBlockModel> {
  resizeable$ = computed(() =>
    this.std.selection.value.some(
      selection =>
        selection.is(ImageSelection) && selection.blockId === this.blockId
    )
  );

  resourceController = new ResourceController(
    computed(() => this.model.props.sourceId$.value),
    'Image'
  );

  // 🔥 CDN 加载状态
  @state()
  private accessor _isLoadingFromCdn = false;

  get blobUrl() {
    return this.resourceController.blobUrl$.value;
  }

  convertToCardView = () => {
    turnImageIntoCardView(this).catch(console.error);
  };

  copy = () => {
    copyImageBlob(this).catch(console.error);
  };

  download = () => {
    downloadImageBlob(this).catch(console.error);
  };

  refreshData = () => {
    refreshData(this).catch(console.error);
  };

  get resizableImg() {
    return this.pageImage?.resizeImg;
  }

  get isCommentHighlighted() {
    return (
      this.std
        .getOptional(BlockElementCommentManager)
        ?.isBlockCommentHighlighted(this.model) ?? false
    );
  }

  /**
   * 🔥 检查是否需要从 CDN 加载图片
   * 条件：sourceId 为空，但 originalUrl 是有效的 HTTP URL
   */
  private _needsLoadFromCdn(): boolean {
    const { sourceId, originalUrl } = this.model.props;
    return !sourceId && !!originalUrl && originalUrl.startsWith('http');
  }

  /**
   * 🔥 从 CDN 加载图片并同步到后端
   */
  private async _loadFromCdn(): Promise<void> {
    const { originalUrl, thumbnailUrl } = this.model.props;
    if (!originalUrl) return;

    // 防止重复加载
    if (pendingCdnLoads.has(this.blockId)) {
      console.log(`🔄 [CDN Load] Already loading: ${this.blockId}`);
      return;
    }
    const urlToFetch = thumbnailUrl || originalUrl;

    pendingCdnLoads.add(this.blockId);
    this._isLoadingFromCdn = true;
    this.resourceController.updateState({ downloading: true });

    try {
      console.log(`🔄 [CDN Load] Starting: ${this.blockId} -> ${originalUrl}`);

      // 🔥 直接请求 CDN URL，不使用代理
      // CDN 图片通常已经配置了 CORS，不需要代理
      // 使用代理反而可能导致 404（代理服务不可用）
      const res = await FetchUtils.fetchImage(
        urlToFetch,
        undefined,
        undefined // 不使用 imageProxy
      );

      if (!res) {
        throw new Error(`Failed to fetch image: ${originalUrl}`);
      }

      // 3. 计算 blobId
      const clonedRes = res.clone();
      const blob = await res.blob();
      const arrayBuf = await clonedRes.arrayBuffer();
      const blobId = await sha(arrayBuf);

      // 4. 获取文件名
      const fileName =
        getFilenameFromContentDisposition(
          res.headers.get('Content-Disposition') ?? ''
        ) ??
        (originalUrl.split('/').at(-1) ?? 'image') +
          '.' +
          (res.headers.get('Content-Type')?.split('/').at(-1) ?? 'png');

      // 5. 创建 File 对象并存入 blob storage
      const file = new File([blob], fileName, {
        type: blob.type || 'image/png',
      });

      // 获取 transformer 并写入 blob
      const transformer = this.std.store.getTransformer();
      const assetsManager = transformer.assetsManager;
      assetsManager.getAssets().set(blobId, file);
      await assetsManager.writeToBlob(blobId);

      // 6. 更新 block 的 sourceId
      this.std.store.updateBlock(this.model, {
        sourceId: blobId,
        size: blob.size,
      });

      console.log(
        `✅ [CDN Load] Complete: ${this.blockId} -> ${blobId.slice(0, 8)}...`
      );
      this.resourceController.updateState({
        errorMessage: null,
      });
    } catch (error) {
      console.error(`❌ [CDN Load] Failed: ${this.blockId}`, error);
      this.resourceController.updateState({
        errorMessage: `Failed to load image`,
      });
    } finally {
      pendingCdnLoads.delete(this.blockId);
      this._isLoadingFromCdn = false;
      this.resourceController.updateState({
        downloading: false,
      });
    }
  }

  private _handleClick(event: MouseEvent) {
    // the peek view need handle shift + click
    if (event.defaultPrevented) return;

    event.stopPropagation();
    const selectionManager = this.host.selection;
    const blockSelection = selectionManager.create(BlockSelection, {
      blockId: this.blockId,
    });
    selectionManager.setGroup('note', [blockSelection]);
  }

  private _initHover() {
    const { setReference, setFloating, dispose } = whenHover(
      hovered => {
        const message$ = this.std.get(ToolbarRegistryIdentifier).message$;
        if (hovered) {
          message$.value = {
            flavour: this.model.flavour,
            element: this,
            setFloating,
          };
          return;
        }

        // Clears previous bindings
        message$.value = null;
        setFloating();
      },
      { enterDelay: 500 }
    );
    setReference(this.hoverableContainer);
    this._disposables.add(dispose);
  }

  override connectedCallback() {
    super.connectedCallback();

    this.contentEditable = 'false';

    this.resourceController.setEngine(this.std.store.blobSync);

    this.disposables.add(this.resourceController.subscribe());
    this.disposables.add(this.resourceController);

    this.disposables.add(
      this.model.props.sourceId$.subscribe(() => {
        this.refreshData();
      })
    );

    // 🔥 检查是否需要从 CDN 加载图片
    if (this._needsLoadFromCdn()) {
      // 延迟执行，确保组件完全初始化
      requestAnimationFrame(() => {
        this._loadFromCdn().catch(console.error);
      });
    } else {
      requestAnimationFrame(() => {
        maybeCompressLocalImageBlob(this).catch(console.error);
      });
    }
  }

  override firstUpdated() {
    // lazy bindings
    this.disposables.addFromEvent(this, 'click', this._handleClick);
    this._initHover();
  }

  override renderBlock() {
    const blobUrl = this.blobUrl;
    const { size = 0 } = this.model.props;

    const containerStyleMap = styleMap({
      position: 'relative',
      width: '100%',
    });

    // 🔥 如果正在从 CDN 加载，强制显示 loading 状态
    const resovledState = this.resourceController.resolveStateWith({
      loadingIcon: LoadingIcon({
        strokeColor: cssVarV2('button/pureWhiteText'),
        ringColor: cssVarV2('loading/imageLoadingLayer', '#ffffff8f'),
      }),
      errorIcon: BrokenImageIcon(),
      icon: ImageIcon(),
      title: 'Image',
      description: this._isLoadingFromCdn ? 'Loading ...' : formatSize(size),
    });

    // 🔥 如果正在从 CDN 加载，覆盖 loading 状态
    if (this._isLoadingFromCdn) {
      resovledState.loading = true;
      resovledState.icon = LoadingIcon({
        strokeColor: cssVarV2('button/pureWhiteText'),
        ringColor: cssVarV2('loading/imageLoadingLayer', '#ffffff8f'),
      });
    }

    return html`
      <div class="affine-image-container" style=${containerStyleMap}>
        ${when(
          blobUrl,
          () =>
            html`<affine-page-image
              .block=${this}
              .state=${resovledState}
            ></affine-page-image>`,
          () =>
            html`<affine-image-fallback-card
              .state=${resovledState}
            ></affine-image-fallback-card>`
        )}
      </div>

      ${Object.values(this.widgets)}
    `;
  }

  override accessor blockContainerStyles = { margin: '18px 0' };

  @query('affine-page-image')
  private accessor pageImage: ImageBlockPageComponent | null = null;

  @query('.affine-image-container')
  accessor hoverableContainer!: HTMLDivElement;

  override accessor useCaptionEditor = true;

  override accessor useZeroWidth = true;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-image': ImageBlockComponent;
  }
}
